import React, { useEffect } from 'react';
// Types
import { APIResponseProps } from 'types/global';
import { ProductionPackSiteProps } from 'types/productionPackSite';
// Utils
import { isEmptyObject, useSetState } from 'utils';
import { mutatePackSiteForModal, isAlphanumeric } from 'utils/packSite';
// Elements
import Button from 'elements/Button';
import { Select, TextField } from 'elements/Forms';
import ToggleItem from 'elements/ListItems/components/ToggleItem';
import { Modal, ModalContent, ModalFooter, ModalHeader } from 'elements/Modal';
import Alert from 'elements/Alert';
// Service
import { getItemDataV1Data } from 'services/itemV1/item';
// Event Context
import { usePackSiteDispatch, usePackSiteState } from 'context/PackSiteContext';
import { patchProdDataDetails, postProdDataDetails } from 'context/PackSiteContext/middleware';
// App Context
import { useAppDispatch } from 'context/AppContext';
import { useAuthState } from 'context/AuthContext';
// Components
import DateDropdown from 'elements/DateDropdown';
import moment from 'moment';
import PackSiteFilter from './PackSiteFilter';
import styles from './style/packsiteModal.module.css';

interface PackSiteModalProps {
  active: boolean,
  handleCancel: Function,
  packSites: ProductionPackSiteProps,
}

function PackSiteModal({
  active,
  handleCancel,
  packSites,
}:PackSiteModalProps) {
  const initialState = {
    roles: [],
    rolesMatrix: false,
    packSiteState: packSites as ProductionPackSiteProps,
    error: '',
    loading: false,
    warningModal: false,
  }
  const [state, setState] = useSetState(initialState);
  const { packDetailFilters } = usePackSiteState();
  const appDispatch = useAppDispatch();
  const packSitesDispatch = usePackSiteDispatch();
  // Auth State
  const { defaultOrganisation: { orgDescription } } = useAuthState();
  const targetList = ['EBS', 'INDEX', 'TROPOS'];
  const setStatus = () => {
    if (state.isNewOrganisation) {
      setState({ 
        packSiteState: {
          ...state.packSiteState,
          ruleType: state.packSiteState.ruleType === 'Multi Site' ? 'Fixed' : 'Multi Site',
          'systemIds': [
            {
              'systemId': 'EBS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : '',
            },
            {
              'systemId': 'RMS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : '',
            },
            {
              'systemId': 'SMS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : '',
            },
          ],
          itemDescription: state.packSiteState.ruleType === 'Multi Site' ? 'All' : '',
        },
        error: '',
      });
    } else {
      setState({ 
        packSiteState: {
          ...state.packSiteState,
          ruleType: state.packSiteState.ruleType === 'Multi Site' ? 'Fixed' : 'Multi Site',
          'systemIds': [
            {
              'systemId': 'EBS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : packSites?.systemIds && packSites.systemIds[0]?.itemId,
            },
            {
              'systemId': 'RMS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : packSites?.systemIds && packSites.systemIds[1]?.itemId,
            },
            {
              'systemId': 'SMS',
              'itemId': state.packSiteState.ruleType === 'Multi Site' ? 'All' : packSites?.systemIds && packSites.systemIds[2]?.itemId,
            },
          ],
          itemDescription: state.packSiteState.ruleType === 'Multi Site' ? 'All' : packSites?.itemDescription,
        },
        error: '',
      })
    }
  }

  function setItemId(itemId: string, packSite: string) {
    if (state.packSiteState.ruleType === 'Fixed') {
      setState({ packSiteState: { ...state.packSiteState, systemIds: [ { itemId: '', systemId: 'EBS' }, state.packSiteState.systemIds[1], state.packSiteState.systemIds[2] ] } });
      setState({ packSiteState: { ...state.packSiteState, itemDescription: '' } })
    } else {
      setState({ packSiteState: { ...state.packSiteState, systemIds: [ { itemId, systemId: 'EBS' }, state.packSiteState.systemIds[1], state.packSiteState.systemIds[2] ] } });
      if (itemId.length >= 8) {
        fetchItemDescription(itemId, packSite);
      }
    }
  }

  async function fetchItemDescription(itemId : string, packSite: string) {
    const { response }: APIResponseProps = await getItemDataV1Data({
      siteRef: packSite,
      itemRef: itemId,
    });
    if (response) {
      setState({ packSiteState: { ...state.packSiteState, itemDescription: response.itemDescription, systemIds: [ { itemId: response.itemRef, systemId: 'EBS' },  state.packSiteState.systemIds[1], state.packSiteState.systemIds[2] ] } })
      setState({
        error: '',
      });
    } else {
      setState({
        error: 'Item description not found',
      });
    }
  }

  function handleExit() {
    setState({
      packSiteState: packSites,
      error: '',
      warningModal: false,
      loading: false,
    });
    handleCancel();
  }

  function handleCancelButton() {
    setState({ warningModal: true });
  }

  async function handleSubmit() {
    if (state.packSiteState.ruleType === 'Multi Site') {
      if (!(state.packSiteState.manufacturingCompany && state.packSiteState.supplierId && state.packSiteState.packSite
      && state.packSiteState.targetERP && state.packSiteState.ruleFromDate && state.packSiteState.distributionDepot
      && state.packSiteState.systemIds[0].itemId !== '' && state.packSiteState.systemIds[1].itemId !== '' && state.packSiteState.systemIds[2].itemId !== '')) {
        setState({
          error: 'Please enter a value for all the fields',
        });
        return;
      }
      if (state.packSiteState.systemIds[0].itemId.length < 8) {
        setState({
          error: 'EBS Item Number must be an 8 digit number',
        });
        return;
      }
      if (!state.packSiteState.itemDescription || state.packSiteState.itemDescription === 'All') {
        setState({
          error: 'Item description not found',
        });
        return;
      }
    }
    if (state.packSiteState.ruleType === 'Fixed') {
      if (!(state.packSiteState.manufacturingCompany && state.packSiteState.supplierId && state.packSiteState.packSite
        && state.packSiteState.targetERP && state.packSiteState.ruleFromDate)) {
        setState({
          error: 'Please enter a value for all the fields',
        });
        return;
      }
    }
    if (!isAlphanumeric(state.packSiteState.packSite) || state.packSiteState.packSite.length !== 3) {
      setState({
        error: 'Pack Site must be 3 alphanumeric characters',
      });
      return;
    }
    setState({
      error: '',
      loading: true,
    });
    if (state.isNewOrganisation) {
      await postProdDataDetails({
        appDispatch,
        packSitesDispatch,
        packDetailFilters,
        packsite: state.packSiteState,
      }); 
    } else {
      await patchProdDataDetails({
        appDispatch,
        packSitesDispatch,
        packDetailFilters,
        packsite: state.packSiteState,
      }); 
    }
    handleExit();
  }

  async function handleSave() {
    setState({ warningModal: false });
  }


  useEffect(() => {
    setState({
      isNewOrganisation: isEmptyObject(packSites),
      packSiteState: !isEmptyObject(packSites) ? mutatePackSiteForModal(packSites) : { 'systemIds': [
        {
          'systemId': 'EBS',
          'itemId': 'All',
        },
        {
          'systemId': 'RMS',
          'itemId': 'All',
        },
        {
          'systemId': 'SMS',
          'itemId': 'All',
        },
      ],
      itemDescription: 'All',
      operatingUnit: orgDescription,
      ruleType: 'Fixed',
      distributionDepot: 'All',
      },
      roles: !isEmptyObject(packSites) ? packSites : [],
    });
  }, [setState, packSites, orgDescription]);

  return (
    <>
    <Modal active={active} id="organisation-modal">
      <div className={styles.alert}>
        { state.error ?
          <Alert
            active={state.error}
            label={state.error}
            type="error"
          /> :
          <Alert
            active={true}
            label="Please Enter All The Values"
            type="info"
          />}
      </div>
      <div className={`${styles.container} ${state.rolesMatrix ? styles.container_matrixActive : ''}`}>
        <div>
          <div className={styles.content}>
            <div>
              <div className={styles.textField}>
                <TextField
                  label="Manfacturing Company"
                  value={state.packSiteState.manufacturingCompany}
                  handler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ packSiteState: { ...state.packSiteState, manufacturingCompany: e.target.value } })}
                />
                <TextField
                  handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ packSiteState: { ...state.packSiteState, supplierId: e.target.value } })}
                  label="Supplier ID"
                  value={state.packSiteState.supplierId}
                />
                <TextField
                  handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ packSiteState: { ...state.packSiteState, packSite: e.target.value } })}
                  label="Pack Site"
                  value={state.packSiteState.packSite}
                />
                <Select
                  handleOnChange={(item: string)  => setState({ packSiteState: { ...state.packSiteState, targetERP: item } })}
                  items={targetList.map(type => type.toUpperCase())}
                  label="Target ERP"
                  name="target-erp"
                  value={state.packSiteState.targetERP}
                />
              </div>
              <div>
                <div className={styles.toggleField}>
                  <ToggleItem
                    checked={state.packSiteState.ruleType === 'Multi Site'}
                    name="creates"
                    handler={() => setStatus()}
                    label="Multi Site Type"
                    value="Multi Site Type"
                  />
                </div>
                <PackSiteFilter
                  label="Effective Date"
                  value={
            (state.packSiteState && state.packSiteState.ruleFromDate)
              ? `${state.packSiteState.ruleFromDate} ${state.packSiteState.ruleToDate ? '-' : ''} ${state.packSiteState.ruleToDate || ''}`
              : 'Select'
          }
                >
                  <div className={styles.dates}>
                    <DateDropdown
                      dateFormat="DD MMM YYYY"
                      isClearable={false}
                      minDate={moment()}
                      maxDate={state.packSiteState.ruleToDate ? moment(state.packSiteState.ruleToDate).subtract(1, 'day') : ''}
                      onChange={(date:string) => setState({
                        packSiteState: { ...state.packSiteState,
                          ruleFromDate: moment(date).format('DD MMM YYYY'),
                        },
                      })}
                      placeholder="From Date"
                      selected={state.packSiteState.ruleFromDate ? moment(state.packSiteState.ruleFromDate) : null}
                      value=""
                    />
                    <DateDropdown
                      dateFormat="DD MMM YYYY"
                      isClearable={true}
                      minDate={state.packSiteState.ruleFromDate ? moment(state.packSiteState.ruleFromDate).add(1, 'day') : moment()}
                      onChange={(date:string) => setState({
                        packSiteState: { ...state.packSiteState,
                          ruleToDate: date ? moment(date).format('DD MMM YYYY') : null,
                        },
                      })}
                      placeholder="To Date"
                      selected={state.packSiteState.ruleToDate ? moment(state.packSiteState.ruleToDate) : null}
                      value=""
                    />
                  </div>
                </PackSiteFilter>
              </div>
            </div>
            <div>
              <TextField
                handler={(e:React.ChangeEvent<HTMLInputElement>)  => setItemId(e.target.value, state.packSiteState.packSite)}
                label="EBS Item"
                disabled={state.packSiteState.ruleType === 'Fixed'}
                value={state.packSiteState.ruleType === 'Fixed' ? 'All' : state.packSiteState?.systemIds && state.packSiteState.systemIds[0]?.itemId}
              />
              <TextField
                label="Item Description"
                disabled={true}
                value={state.packSiteState.ruleType === 'Fixed' ? 'All' : state.packSiteState.itemDescription}
              />
              <TextField
                handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ packSiteState: { ...state.packSiteState, systemIds: [ state.packSiteState.systemIds[0], { itemId: (state.packSiteState.ruleType === 'Fixed') ? '' : e.target.value, systemId: 'RMS' }, state.packSiteState.systemIds[2] ] } })}
                label="PIN Code"
                disabled={state.packSiteState.ruleType === 'Fixed'}
                value={state.packSiteState.ruleType === 'Fixed' ? 'All' : state.packSiteState?.systemIds && state.packSiteState.systemIds[1]?.itemId}
              />
              <TextField
                handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ packSiteState: { ...state.packSiteState, systemIds: [ state.packSiteState.systemIds[0], state.packSiteState.systemIds[1], { itemId: (state.packSiteState.ruleType === 'Fixed') ? '' : e.target.value, systemId: 'SMS' } ] } })}
                label="SMS Item"
                disabled={state.packSiteState.ruleType === 'Fixed'}
                value={state.packSiteState.ruleType === 'Fixed' ? 'All' : state.packSiteState?.systemIds && state.packSiteState.systemIds[2]?.itemId}
              />
              <TextField
                handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ packSiteState: { ...state.packSiteState, distributionDepot: (state.packSiteState.ruleType === 'Fixed') ? 'All' : e.target.value } })}
                label="Distribution Depot"
                disabled={state.packSiteState.ruleType === 'Fixed'}
                value={state.packSiteState.ruleType === 'Fixed' ? 'All' : state.packSiteState.distributionDepot}
              />
              <div className={styles.footer}>
                <Button handler={() => handleCancelButton()}>
                  Cancel
                </Button>
                <Button
                  btnStyle="secondary"
                  disabled={state.loading}
                  handler={() => handleSubmit()}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <Modal active={state.warningModal}>
    <ModalHeader heading="Warning" />
    <ModalContent>
    Note: Updates have not been saved. Are you sure you want to exit?
    </ModalContent>
    <ModalFooter>
      <Button handler={() => handleExit()}>
        Yes
      </Button>
      <Button
        btnStyle="secondary"
        handler={() => handleSave()}
      >
        No
      </Button>
    </ModalFooter>
  </Modal>
  </>
  );
}

export default PackSiteModal
